import {useEffect, useRef} from 'react';

import {useCalculateFromWithoutVatQuery, useCalculateFromWithVatQuery} from '@dms/api/core';
import {VatRateType} from '@dms/api/shared';

interface CalculatePriceProps {
  amount: string;
  countryCode?: string;
  vatCode: VatRateType;
  type?: string;
  onChange?: (res: {withVat?: string} | {withoutVat?: string}) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function CalculatePrice(props: CalculatePriceProps) {
  const {onLoadingChange} = props;
  const isMounted = useRef<boolean>(false);

  const areFetchingPropsValid = !!props.amount && !!props.vatCode;

  const {
    currentData: fromWithVatData,
    isLoading: isLoadingWithVat,
    isFetching: isFetchingWithVat,
  } = useCalculateFromWithVatQuery(
    {
      amount: props.amount,
      vatRateType: props.vatCode,
      countryCode: props.countryCode ?? '',
    },
    {
      skip: props.type !== 'withVat' || !areFetchingPropsValid,
    }
  );

  const {
    currentData: fromWithoutVatData,
    isLoading: isLoadingWithoutVat,
    isFetching: isFetchingWithoutVat,
  } = useCalculateFromWithoutVatQuery(
    {
      amount: props.amount,
      vatRateType: props.vatCode,
      countryCode: props.countryCode ?? '',
    },
    {
      skip: props.type !== 'withoutVat' || !areFetchingPropsValid,
    }
  );

  // eslint-disable-next-line eag/no-effect-without-cleanup
  useEffect(() => {
    isMounted.current = true;
  }, []);

  useEffect(() => {
    if (!(isMounted.current && fromWithVatData)) {
      return;
    }

    props.onChange?.({withoutVat: fromWithVatData.withoutVat});
  }, [fromWithVatData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!(isMounted.current && fromWithoutVatData)) {
      return;
    }

    props.onChange?.({withVat: fromWithoutVatData.withVat});
  }, [fromWithoutVatData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isLoading =
      isLoadingWithVat || isLoadingWithoutVat || isFetchingWithVat || isFetchingWithoutVat;

    if (isMounted.current && onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [
    isLoadingWithVat,
    isLoadingWithoutVat,
    isFetchingWithVat,
    isFetchingWithoutVat,
    onLoadingChange,
  ]);

  return null;
}
